import React, { useState, useEffect, useRef } from 'react';
import { 
    Input, 
    Button, 
    Card, 
    Row, 
    Col, 
    Select, 
    Slider, 
    Checkbox, 
    Tag, 
    Space, 
    Typography, 
    Divider,
    Tooltip,
    Badge,
    Drawer,
    Collapse
} from 'antd';
import { 
    SearchOutlined, 
    FilterOutlined, 
    ClearOutlined, 
    StarOutlined,
    DollarOutlined,
    EnvironmentOutlined,
    FireOutlined,
    HistoryOutlined,
    SoundOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { useSearch } from '../../contexts/SearchContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const AdvancedSearch = ({ 
    onSearch, 
    placeholder = "Search vendors, dishes, cuisines...",
    showFilters = true,
    showSuggestions: enableSuggestions = true,
    showTrending = true,
    compact = false
}) => {
    const { 
        searchSuggestions, 
        trendingSearches, 
        popularFilters, 
        currentFilters, 
        getSuggestions, 
        updateFilters, 
        clearFilters, 
        getActiveFiltersCount,
        getFilterSummary,
        voiceSearch,
        loading
    } = useSearch();
    
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilterDrawer, setShowFilterDrawer] = useState(false);
    const [showSuggestionsDropdown, setShowSuggestionsDropdown] = useState(false);
    const [voiceSearching, setVoiceSearching] = useState(false);
    const searchInputRef = useRef(null);

    const activeFiltersCount = getActiveFiltersCount();
    const filterSummary = getFilterSummary();

    // Handle search input change
    const handleSearchChange = async (value) => {
        setSearchQuery(value);
        
        if (value.length >= 2) {
            await getSuggestions(value);
            setShowSuggestionsDropdown(true);
        } else {
            setShowSuggestionsDropdown(false);
        }
    };

    // Handle search submission
    const handleSearch = (query = searchQuery) => {
        if (query.trim()) {
            updateFilters({ search: query.trim() });
            onSearch?.(query.trim());
            setShowSuggestionsDropdown(false);
        }
    };

    // Handle suggestion click
    const handleSuggestionClick = (suggestion) => {
        setSearchQuery(suggestion);
        handleSearch(suggestion);
    };

    // Handle trending search click
    const handleTrendingClick = (trending) => {
        setSearchQuery(trending.query);
        handleSearch(trending.query);
    };

    // Handle voice search
    const handleVoiceSearch = async () => {
        try {
            setVoiceSearching(true);
            const query = await voiceSearch();
            setSearchQuery(query);
            handleSearch(query);
        } catch (error) {
            console.error('Voice search failed:', error);
        } finally {
            setVoiceSearching(false);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        updateFilters({ [key]: value });
    };

    // Handle clear filters
    const handleClearFilters = () => {
        clearFilters();
        setSearchQuery('');
    };

    // Handle key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        } else if (e.key === 'Escape') {
            setShowSuggestionsDropdown(false);
        }
    };

    // Render search input
    const renderSearchInput = () => (
        <div className="relative">
            <Input
                ref={searchInputRef}
                size="large"
                placeholder={placeholder}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => searchQuery.length >= 2 && setShowSuggestionsDropdown(true)}
                onBlur={() => setTimeout(() => setShowSuggestionsDropdown(false), 200)}
                prefix={<SearchOutlined className="text-gray-400" />}
                suffix={
                    <Space>
                        {/* {voiceSearching ? (
                            <Button 
                                type="text" 
                                icon={<ReloadOutlined spin />} 
                                size="small"
                            />
                        ) : (
                            <Tooltip title="Voice Search">
                                <Button 
                                    type="text" 
                                    icon={<SoundOutlined />} 
                                    size="small"
                                    onClick={handleVoiceSearch}
                                />
                            </Tooltip>
                        )} */}
                        <Button 
                            type="primary" 
                            onClick={() => handleSearch()}
                            loading={loading}
                        >
                            Search
                        </Button>
                    </Space>
                }
                className="w-full"
            />
        </div>
    );

    // Render suggestions dropdown
    const renderSuggestions = () => {
        if (!enableSuggestions || !showSuggestionsDropdown || searchSuggestions.length === 0) return null;

        return (
            <Card 
                className="absolute top-full left-0 right-0 z-50 mt-1 bg-white"
                bodyStyle={{ padding: '8px 0' }}
            >
                <div className="max-h-60 overflow-y-auto">
                    {searchSuggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleSuggestionClick(suggestion)}
                        >
                            <Text>{suggestion}</Text>
                        </div>
                    ))}
                </div>
            </Card>
        );
    };

    // Render trending searches
    const renderTrendingSearches = () => {
        if (!showTrending || trendingSearches.length === 0) return null;

        return (
            <div className="mt-4">
                <Title level={5} className="flex items-center gap-2">
                    <FireOutlined className="text-orange-500" />
                    Trending Searches
                </Title>
                <div className="flex flex-wrap gap-2">
                    {trendingSearches.slice(0, 8).map((trending, index) => (
                        <Tag
                            key={index}
                            className="cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900"
                            onClick={() => handleTrendingClick(trending)}
                        >
                            {trending.query}
                            <Badge count={trending.count} size="small" className="ml-1" />
                        </Tag>
                    ))}
                </div>
            </div>
        );
    };

    // Render filter drawer
    const renderFilterDrawer = () => (
        <Drawer
            title="Advanced Filters"
            placement="right"
            onClose={() => setShowFilterDrawer(false)}
            open={showFilterDrawer}
            width={400}
            extra={
                <Button 
                    type="text" 
                    icon={<ClearOutlined />}
                    onClick={handleClearFilters}
                >
                    Clear All
                </Button>
            }
        >
            <div className="space-y-6">
                {/* City Filter */}
                <div>
                    <Title level={5} className="flex items-center gap-2">
                        <EnvironmentOutlined />
                        City
                    </Title>
                    <Select
                        placeholder="Select city"
                        value={currentFilters.city_id}
                        onChange={(value) => handleFilterChange('city_id', value)}
                        className="w-full"
                        allowClear
                    >
                        {popularFilters.cities?.map(city => (
                            <Option key={city.id} value={city.id}>
                                {city.name}
                            </Option>
                        ))}
                    </Select>
                </div>

                {/* Cuisine Filter */}
                <div>
                    <Title level={5}>Cuisine</Title>
                    <Select
                        placeholder="Select cuisine"
                        value={currentFilters.cuisine}
                        onChange={(value) => handleFilterChange('cuisine', value)}
                        className="w-full"
                        allowClear
                    >
                        {popularFilters.cuisines?.map(cuisine => (
                            <Option key={cuisine} value={cuisine}>
                                {cuisine}
                            </Option>
                        ))}
                    </Select>
                </div>

                {/* Price Range */}
                <div>
                    <Title level={5} className="flex items-center gap-2">
                        <DollarOutlined />
                        Price Range
                    </Title>
                    <div className="space-y-2">
                        <Row gutter={8}>
                            <Col span={12}>
                                <Input
                                    placeholder="Min"
                                    value={currentFilters.min_order}
                                    onChange={(e) => handleFilterChange('min_order', e.target.value)}
                                    prefix="₹"
                                />
                            </Col>
                            <Col span={12}>
                                <Input
                                    placeholder="Max"
                                    value={currentFilters.max_order}
                                    onChange={(e) => handleFilterChange('max_order', e.target.value)}
                                    prefix="₹"
                                />
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Rating Filter */}
                <div>
                    <Title level={5} className="flex items-center gap-2">
                        <StarOutlined />
                        Minimum Rating
                    </Title>
                    <Select
                        placeholder="Select rating"
                        value={currentFilters.rating}
                        onChange={(value) => handleFilterChange('rating', value)}
                        className="w-full"
                        allowClear
                    >
                        {popularFilters.ratings?.map(rating => (
                            <Option key={rating.min} value={rating.min}>
                                {rating.label}
                            </Option>
                        ))}
                    </Select>
                </div>

                {/* Dietary Options */}
                <div>
                    <Title level={5}>Dietary Options</Title>
                    <Checkbox.Group
                        value={currentFilters.dietary_options}
                        onChange={(values) => handleFilterChange('dietary_options', values)}
                        className="w-full"
                    >
                        <Row gutter={[8, 8]}>
                            {popularFilters.dietaryOptions?.map(option => (
                                <Col span={12} key={option}>
                                    <Checkbox value={option}>{option}</Checkbox>
                                </Col>
                            ))}
                        </Row>
                    </Checkbox.Group>
                </div>

                {/* Features */}
                <div>
                    <Title level={5}>Features</Title>
                    <Checkbox.Group
                        value={currentFilters.features}
                        onChange={(values) => handleFilterChange('features', values)}
                        className="w-full"
                    >
                        <Row gutter={[8, 8]}>
                            {popularFilters.features?.map(feature => (
                                <Col span={12} key={feature}>
                                    <Checkbox value={feature}>{feature}</Checkbox>
                                </Col>
                            ))}
                        </Row>
                    </Checkbox.Group>
                </div>
            </div>
        </Drawer>
    );

    // Render active filters
    const renderActiveFilters = () => {
        if (activeFiltersCount === 0) return null;

        return (
            <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                    <Text strong>Active Filters ({activeFiltersCount})</Text>
                    <Button 
                        type="text" 
                        size="small" 
                        icon={<ClearOutlined />}
                        onClick={handleClearFilters}
                    >
                        Clear All
                    </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {filterSummary.map((filter, index) => (
                        <Tag 
                            key={index} 
                            closable 
                            onClose={() => {
                                // Handle individual filter removal
                                const filterKey = filter.split(':')[0].toLowerCase().replace(' ', '_');
                                handleFilterChange(filterKey, '');
                            }}
                        >
                            {filter}
                        </Tag>
                    ))}
                </div>
            </div>
        );
    };

    if (compact) {
        return (
            <div className="relative">
                {renderSearchInput()}
                {renderSuggestions()}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Search Input */}
            <div className="relative">
                {renderSearchInput()}
                {renderSuggestions()}
            </div>

            {/* Filter Button */}
            {showFilters && (
                <div className="flex items-center justify-between">
                    <Button
                        icon={<FilterOutlined />}
                        onClick={() => setShowFilterDrawer(true)}
                    >
                        Filters
                        {activeFiltersCount > 0 && (
                            <Badge count={activeFiltersCount} size="small" />
                        )}
                    </Button>
                    
                    {activeFiltersCount > 0 && (
                        <Button 
                            type="text" 
                            icon={<ClearOutlined />}
                            onClick={handleClearFilters}
                        >
                            Clear Filters
                        </Button>
                    )}
                </div>
            )}

            {/* Active Filters */}
            {renderActiveFilters()}

            {/* Trending Searches */}
            {renderTrendingSearches()}

            {/* Filter Drawer */}
            {renderFilterDrawer()}
        </div>
    );
};

export default AdvancedSearch;
